// AIDialerCall.jsx — Phase 3B AI Dialer Call Detail
// Transcript, cost breakdown, outcome, DNC button

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Phone, PhoneOff, User, Bot, Clock, DollarSign, CheckCircle2,
  XCircle, Loader2, ChevronLeft, Calendar, ShieldX, BarChart3,
  Volume2, FileText, AlertCircle, Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { useAuth } from '../store/auth.js';

const OUTCOME_CONFIG = {
  booked_appointment: { label: 'Booked Appointment', color: 'text-civix-cyan',  bg: 'bg-cyan-500/10',    icon: Calendar },
  not_interested:     { label: 'Not Interested',     color: 'text-red-400',     bg: 'bg-red-400/10',     icon: XCircle  },
  callback_later:     { label: 'Callback Later',     color: 'text-yellow-400',  bg: 'bg-yellow-400/10',  icon: Clock    },
  wrong_number:       { label: 'Wrong Number',       color: 'text-white/50',    bg: 'bg-white/5',        icon: PhoneOff },
  dnc_blocked:        { label: 'DNC Blocked',        color: 'text-orange-400',  bg: 'bg-orange-400/10',  icon: ShieldX  },
  do_not_call:        { label: 'Do Not Call',        color: 'text-orange-400',  bg: 'bg-orange-400/10',  icon: ShieldX  },
  voicemail_left:     { label: 'Voicemail Left',     color: 'text-blue-400',    bg: 'bg-blue-400/10',    icon: Volume2  },
  failed:             { label: 'Failed',             color: 'text-red-500',     bg: 'bg-red-500/10',     icon: AlertCircle },
};

function fmt(v) {
  if (v == null || isNaN(v)) return '$0.000';
  return `$${Number(v).toFixed(4)}`;
}

function fmtTime(secs) {
  if (!secs) return '0:00';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function DncConfirmModal({ phone, busy, onConfirm, onCancel }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', onKey);
    cancelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dnc-confirm-title"
      className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4"
      onClick={onCancel}
    >
      <div onClick={(e) => e.stopPropagation()} className="panel p-5 max-w-sm w-full space-y-4">
        <div id="dnc-confirm-title" className="font-display font-bold text-lg text-white flex items-center gap-2">
          <ShieldX className="w-5 h-5 text-orange-400" /> Add to DNC list?
        </div>
        <div className="text-sm text-white/70 space-y-2">
          <p>
            <span className="text-white font-medium">{phone}</span> will be blocked from <em>all</em> future AI Dialer campaigns across your workspace.
          </p>
          <p className="text-xs text-white/50">This is required by federal TCPA rules once a lead asks not to be called. It can be reversed only from the DNC management page.</p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button ref={cancelRef} onClick={onCancel} disabled={busy} className="btn-ghost">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-3 py-2 rounded-md text-sm font-medium text-white bg-rose-500/80 hover:bg-rose-500"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : 'Add to DNC'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AIDialerCall() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [generatingScenario, setGeneratingScenario] = useState(false);
  const [confirmDnc, setConfirmDnc] = useState(false);

  const { data: callData, isLoading, isError, error: loadError } = useQuery({
    queryKey: ['ai-dialer-call', id],
    queryFn: () => apiGet(`/ai-dialer/calls/${id}`),
    staleTime: 5000,
    retry: false,
  });

  const call = callData?.data || callData || {};
  // call.leadId can be a populated object or a raw ObjectId string depending
  // on backend response shape. Guard against the string case.
  const lead = (call.leadId && typeof call.leadId === 'object') ? call.leadId : {};
  const oc   = OUTCOME_CONFIG[call.outcome] || {};
  const OutcomeIcon = oc.icon || Phone;

  const dncMutation = useMutation({
    mutationFn: () => apiPost('/ai-dialer/dnc', { phone: lead.phone, reason: 'do_not_call_disposition' }),
    onSuccess: () => {
      toast.success(`${lead.phone} added to DNC list`);
      setConfirmDnc(false);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to add DNC'),
  });

  async function generateScenario() {
    setGeneratingScenario(true);
    try {
      const r = await apiPost('/roleplay/generate-from-call', { dialerCallId: id });
      const name = r?.data?.persona?.name || 'Auto-generated scenario';
      toast.success(`New scenario "${name}" added to roleplay library`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to generate scenario');
    }
    setGeneratingScenario(false);
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <TopBar title="Call Detail" subtitle="AI Dialer" />
        <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 text-civix-purple animate-spin" /></div>
      </div>
    );
  }

  if (isError || !call._id) {
    return (
      <div className="space-y-4">
        <TopBar title="Call not found" subtitle="AI Dialer · Call Detail" />
        <div className="panel p-10 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-400/60 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">We couldn't load that call</h3>
          <p className="text-white/50 text-sm mb-6 max-w-md mx-auto">
            {loadError?.response?.data?.error
              || 'The call may have been deleted, archived, or you may not have access to it.'}
          </p>
          <button
            onClick={() => nav('/ai-dialer')}
            className="btn-primary inline-flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" /> Back to AI Dialer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <TopBar
        title={`Call — ${lead.firstName || ''} ${lead.lastName || ''}`}
        subtitle="AI Dialer · Call Detail"
        actions={
          <button onClick={() => nav(-1)} className="btn-ghost text-sm flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        {/* Left: Transcript */}
        <div className="space-y-4">
          {/* Outcome badge */}
          {call.outcome && (
            <div className={`panel p-4 flex items-center gap-3 ${oc.bg}`}>
              <OutcomeIcon className={`w-6 h-6 ${oc.color} shrink-0`} />
              <div>
                <div className={`font-semibold ${oc.color}`}>{oc.label || call.outcome}</div>
                {call.appointmentId && (
                  <div className="text-xs text-white/50">Appointment created</div>
                )}
              </div>
              {call.stubMode && (
                <span className="ml-auto text-xs text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded-full">Stub Mode</span>
              )}
            </div>
          )}

          {/* Transcript */}
          <div className="panel p-4">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-civix-purple" />
              <h3 className="text-base font-semibold text-white">Transcript</h3>
              <span className="text-xs text-white/40 ml-auto">{call.transcript?.length || 0} lines</span>
            </div>

            {(!call.transcript || call.transcript.length === 0) ? (
              <div className="text-center py-10">
                <FileText className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <div className="text-sm font-medium text-white/70 mb-1">No transcript yet</div>
                <div className="text-xs text-white/40 max-w-xs mx-auto">
                  Transcripts are written live during the call. If the call just ended, give it a few seconds and refresh.
                </div>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {call.transcript.map((line, i) => {
                  const isAI     = line.speaker === 'ai';
                  const isLead   = line.speaker === 'lead';
                  const isSystem = line.speaker === 'system';
                  return (
                    <div key={i} className={`flex gap-3 ${isAI ? '' : 'flex-row-reverse'} ${isSystem ? 'flex-row! justify-center' : ''}`}>
                      {!isSystem && (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
                          isAI ? 'bg-civix-purple/20 text-civix-purple' : 'bg-white/10 text-white/70'
                        }`}>
                          {isAI ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
                        </div>
                      )}
                      <div className={`flex-1 ${isSystem ? 'text-center' : ''}`}>
                        {!isSystem && (
                          <div className={`text-xs mb-1 ${isAI ? 'text-civix-purple' : 'text-white/40'}`}>
                            {isAI ? 'AI Agent' : 'Lead'}
                            {line.at !== undefined && <span className="ml-2 text-white/30">{fmtTime(line.at)}</span>}
                          </div>
                        )}
                        <div className={`inline-block px-3 py-2 rounded-xl text-sm max-w-xs lg:max-w-sm ${
                          isSystem ? 'bg-white/5 text-white/40 text-xs italic px-4' :
                          isAI     ? 'bg-civix-purple/15 text-white border border-civix-purple/20' :
                                     'bg-civix-panelLight text-white/80 border border-civix-border'
                        }`}>
                          {line.text}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Details */}
        <div className="space-y-4">
          {/* Lead info */}
          <div className="panel p-4 space-y-3">
            <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider">Lead</h3>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-civix-gradient flex items-center justify-center text-sm font-bold">
                {(lead.firstName?.[0] || '?')}
              </div>
              <div>
                <div className="text-sm font-semibold text-white">{lead.firstName} {lead.lastName}</div>
                <div className="text-xs text-white/40">{lead.phone}</div>
                <div className="text-xs text-white/40">{lead.state} · {lead.email}</div>
              </div>
            </div>
          </div>

          {/* Call metadata */}
          <div className="panel p-4 space-y-2">
            <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Call Info</h3>
            {[
              { label: 'Status',    value: call.status },
              { label: 'Duration',  value: fmtTime(call.durationSeconds) },
              { label: 'Started',   value: call.startedAt ? new Date(call.startedAt).toLocaleString() : '—' },
              { label: 'Disclosure', value: call.disclosureGiven ? '✓ Given' : '✗ Not given' },
              { label: 'Consent',   value: call.consentToProceed ? '✓ Consented' : '✗ Not consented' },
              { label: 'Twilio SID', value: call.twilioCallSid || 'N/A' },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-xs text-white/40">{label}</span>
                <span className="text-xs font-medium text-white">{value || '—'}</span>
              </div>
            ))}
          </div>

          {/* Cost breakdown */}
          {call.costBreakdown && (
            <div className="panel p-4">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="w-4 h-4 text-civix-purple" />
                <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider">Cost</h3>
              </div>
              <div className="space-y-1.5">
                {[
                  { label: 'Twilio Voice',   value: call.costBreakdown.twilioVoice },
                  { label: 'ElevenLabs TTS', value: call.costBreakdown.elevenLabs },
                  { label: 'OpenAI Brain',   value: call.costBreakdown.openaiBrain },
                  { label: 'Whisper STT',    value: call.costBreakdown.openaiTranscription },
                  { label: 'Perplexity',     value: call.costBreakdown.perplexitySearch },
                ].filter((r) => r.value > 0).map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-xs text-white/40">{label}</span>
                    <span className="text-xs font-mono text-white">{fmt(value)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-civix-border">
                  <span className="text-xs font-semibold text-white">Total</span>
                  <span className="text-sm font-bold text-civix-cyan">{fmt(call.costBreakdown.total)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="panel p-4 space-y-2">
            {call.appointmentId && (
              <div className="flex items-center gap-2 p-2 bg-civix-cyan/10 rounded-lg border border-civix-cyan/20">
                <Calendar className="w-4 h-4 text-civix-cyan" />
                <span className="text-xs text-civix-cyan font-medium">Appointment created</span>
              </div>
            )}
            {isAdmin() && call.transcript?.length > 0 && (
              <button
                onClick={generateScenario}
                disabled={generatingScenario}
                className="w-full btn-ghost text-civix-purple text-sm flex items-center justify-center gap-2 hover:border-civix-purple/60!"
              >
                {generatingScenario ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Convert to Roleplay Scenario
              </button>
            )}
            <button
              onClick={() => setConfirmDnc(true)}
              disabled={dncMutation.isPending || !lead.phone}
              className="w-full btn-ghost text-red-400 text-sm flex items-center justify-center gap-2"
              title={!lead.phone ? 'No phone number on this lead' : 'Block this number from all future AI Dialer campaigns'}
            >
              {dncMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldX className="w-4 h-4" />}
              Add to DNC
            </button>
          </div>
        </div>
      </div>
      {confirmDnc && (
        <DncConfirmModal
          phone={lead.phone}
          busy={dncMutation.isPending}
          onConfirm={() => dncMutation.mutate()}
          onCancel={() => !dncMutation.isPending && setConfirmDnc(false)}
        />
      )}
    </div>
  );
}
